/**
 * GA4 (G-W3Q4FEM68C) — the single owner of Google Analytics for this site.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * GA4 had no owner at all. `public/index.html` said "GA4 is loaded directly by
 * the app, do NOT add a GA4 tag to GTM"; `src/index.js` said "GA4 is loaded by
 * GTM, do not re-add a direct init". Each file deferred to the other, the
 * react-ga4 init had been deleted, and the published GTM container
 * (GTM-MVTPG3QM) contains only a Google Ads tag (AW-17983919375) — no
 * __gaawc/__gaawe. Verified against the live container JS and with a real
 * browser: gtm.js loaded, Ads fired, and /g/collect was never called. Hence
 * "No data received from your website yet".
 *
 * The comment in index.html is the intent we honour: GA4 lives here, in the
 * app. Do NOT also add a GA4 Configuration tag to GTM — that would double
 * every page_view and every conversion.
 *
 * DESIGN
 * ------
 * - Loads gtag.js ONCE, lazily, on the same "idle or first interaction" gate
 *   the GTM snippet already uses, so analytics never competes with the hero
 *   paint. A visitor who taps straight into the booking form is still measured.
 * - `send_page_view: false` on config. This is a React Router SPA: gtag's
 *   automatic page_view fires once for the document and then never again, so
 *   route changes would be invisible. We send every page_view ourselves,
 *   including the first, which keeps one code path for all of them.
 * - Language is read from the URL (/fr, /en, /ar) and sent as an explicit
 *   dimension so EN/FR/AR traffic is separable in reports.
 * - Never throws. Analytics must not be able to break a booking.
 *
 * This module deliberately does NOT touch the booking flow. Commerce events
 * continue to go through SharedComponent/tracking.js -> window.dataLayer ->
 * GTM -> Google Ads, exactly as before.
 */

export const GA4_MEASUREMENT_ID = "G-W3Q4FEM68C";

/** /fr/... -> "fr". Defaults to "fr" (the site's primary market). */
const langFromPath = (pathname) => {
  const seg = String(pathname || "").split("/")[1]?.toLowerCase();
  return ["fr", "en", "ar"].includes(seg) ? seg : "fr";
};

/**
 * The gtag queue must be created before gtag.js arrives, and the array must
 * never be reassigned afterwards, or queued calls are silently dropped.
 */
function ensureGtag() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

let scriptRequested = false;
let configured = false;
/** page_views that happened before gtag.js finished loading. */
const pending = [];
/**
 * The last page_path actually sent.
 *
 * Guards against double counting from any caller. Two real sources exist:
 * initGA4() queues the first page_view, and App.js's effect also fires on
 * mount; React may additionally re-run an effect (StrictMode, or a dependency
 * settling a tick later). Measured before this guard: every SPA navigation
 * produced two page_view hits, the first with an empty page_path. GA4 has no
 * server-side dedupe, so this has to be enforced here.
 */
let lastPath = null;

function injectScript() {
  if (scriptRequested) return;
  scriptRequested = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(s);
}

function configure() {
  if (configured) return;
  const gtag = ensureGtag();
  if (!gtag) return;
  configured = true;

  gtag("js", new Date());
  gtag("config", GA4_MEASUREMENT_ID, {
    // We own page_view (SPA — see the header comment).
    send_page_view: false,
    // Cookies work across www/apex without per-host duplication.
    cookie_flags: "SameSite=None;Secure",
    anonymize_ip: true,
  });

  injectScript();

  // Replay the route the visitor is actually on. Queued before the idle gate,
  // sent now that gtag is configured.
  const queued = pending.shift();
  pending.length = 0;
  sendPageView(queued ?? {});
}

/**
 * Send one page_view.
 *
 * Called for the initial load AND for every SPA route change, so the payload
 * shape is identical in both cases.
 */
export function sendPageView({ path, title, language } = {}) {
  try {
    if (typeof window === "undefined") return;
    // Read from the live location when no explicit path is given. An empty
    // string must not win here: a caller passing "" used to send a page_view
    // with no page_path at all.
    const pathname = (path && String(path).trim())
      || (window.location.pathname + window.location.search);

    if (!configured) {
      // Not initialised yet (still before the idle gate) — remember the most
      // recent route only; queueing several would replay stale views.
      pending.length = 0;
      pending.push({ path: pathname, title, language });
      return;
    }

    // Same route twice in a row is always a duplicate, never a real second view.
    if (pathname === lastPath) return;
    lastPath = pathname;

    const gtag = ensureGtag();
    if (!gtag) return;
    gtag("event", "page_view", {
      page_path: pathname,
      page_location: window.location.href,
      page_title: title ?? document.title,
      language: language ?? langFromPath(window.location.pathname),
    });
  } catch (e) {
    /* best-effort: analytics must never break the page */
  }
}

/**
 * Start GA4.
 *
 * Mirrors the GTM snippet's loading policy exactly — idle, or the first real
 * interaction, whichever comes first — so we add no render-blocking work and
 * no new "who loads first" race.
 */
export function initGA4() {
  try {
    if (typeof window === "undefined") return;
    if (scriptRequested || configured) return;

    // Queue the first page_view immediately; it flushes on configure().
    sendPageView();

    const EVENTS = ["pointerdown", "keydown", "scroll", "touchstart"];
    const onFirstInteraction = () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onFirstInteraction, true));
      configure();
    };
    EVENTS.forEach((e) =>
      window.addEventListener(e, onFirstInteraction, {
        capture: true, passive: true, once: true,
      }));

    if ("requestIdleCallback" in window) {
      /* Wait for `load` BEFORE going idle. Measured on live production:
         gtag.js is 191 KB and it was starting at 1587ms — i.e. inside the
         LCP window (3.4s) and while the 394 KB app bundle was still being
         evaluated, so its parse/compile landed straight in Total Blocking
         Time. Idle alone is not "after the page has loaded" on a throttled
         phone; idle fires during the quiet gaps of the load itself. The GTM
         snippet in index.html carries the same gate for the same reason.
         First interaction still short-circuits this, so a visitor who taps
         immediately is measured immediately. */
      const idle = () => {
        const run = () => window.requestIdleCallback(configure, { timeout: 5000 });
        if (process.env.REACT_APP_SCOPED_SPEED === 'true') setTimeout(run, 4000);
        else run();
      };
      if (document.readyState === "complete") idle();
      else window.addEventListener("load", idle, { once: true });
    } else {
      window.addEventListener("load", () => setTimeout(configure, 2500));
    }
  } catch (e) {
    /* best-effort */
  }
}
